/**
 * AuthContext - Authentication State Context
 *
 * Defines the shape of the authentication context that is provided
 * throughout the application. This context is provider-agnostic and
 * works with any AuthAdapter implementation.
 */

import { createContext } from 'react';
import type { AuthUser, AuthProviderType, LoginOptions } from './adapters';

/**
 * Authentication state exposed via context
 */
export interface AuthState {
  /** Whether authentication is currently being initialized */
  isLoading: boolean;

  /** Whether a user is currently authenticated */
  isAuthenticated: boolean;

  /** The currently authenticated user, or null if not authenticated */
  user: AuthUser | null;

  /** Roles assigned to the current user */
  roles: string[];

  /** Any authentication error that occurred */
  error: Error | null;

  /** The currently active authentication provider type */
  providerType: AuthProviderType | null;
}

/**
 * Authentication actions available via context
 */
export interface AuthActions {
  /** Initiates the login flow */
  login: (options?: LoginOptions) => Promise<void>;

  /** Registers a new user with email and password */
  register: (email: string, password: string) => Promise<void>;

  /** Signs out the current user */
  logout: () => Promise<void>;

  /** Refreshes the current user's data and tokens */
  refreshUser: () => Promise<void>;

  /** Clears any authentication error */
  clearError: () => void;

  /** Switches to a different authentication provider */
  switchProvider: (providerType: AuthProviderType) => void;
}

/**
 * Combined authentication context value
 */
export interface AuthContextValue extends AuthState, AuthActions {}

/**
 * Default context value (used before provider is initialized)
 */
const defaultContextValue: AuthContextValue = {
  // State
  isLoading: true,
  isAuthenticated: false,
  user: null,
  roles: [],
  error: null,
  providerType: null,

  // Actions (no-ops until provider is mounted)
  login: async () => {
    console.warn('AuthContext: login called before provider initialized');
  },
  register: async () => {
    console.warn('AuthContext: register called before provider initialized');
  },
  logout: async () => {
    console.warn('AuthContext: logout called before provider initialized');
  },
  refreshUser: async () => {
    console.warn('AuthContext: refreshUser called before provider initialized');
  },
  clearError: () => {
    console.warn('AuthContext: clearError called before provider initialized');
  },
  switchProvider: () => {
    console.warn('AuthContext: switchProvider called before provider initialized');
  },
};

/**
 * React Context for authentication state and actions
 *
 * Usage:
 * ```tsx
 * // In a component
 * const { isAuthenticated, user, login, logout } = useAuth();
 * ```
 */
export const AuthContext = createContext<AuthContextValue>(defaultContextValue);

// Set display name for React DevTools
AuthContext.displayName = 'AuthContext';
